import type { FunctionComponent } from 'react';

export type ToolIconKind = 'read' | 'write' | 'email' | 'agent' | 'pipeline' | 'refresh';

interface ToolDisplay {
  label: string;
  icon: ToolIconKind;
}

const TOOL_LABELS: Record<string, ToolDisplay> = {
  get_sponsor_summary: { label: 'Reading the sponsor summary', icon: 'read' },
  query_participants: { label: 'Checking participants', icon: 'read' },
  query_pay_periods: { label: 'Checking pay periods', icon: 'read' },
  query_exceptions: { label: 'Checking exceptions', icon: 'read' },
  query_test_results: { label: 'Checking test results', icon: 'read' },
  query_corrections: { label: 'Checking corrections', icon: 'read' },
  query_distributions: { label: 'Checking distributions', icon: 'read' },
  get_plan_rules: { label: 'Reading the plan rules', icon: 'read' },
  list_uploads: { label: 'Checking uploads', icon: 'read' },
  list_sponsors: { label: 'Checking sponsors', icon: 'read' },
  getFtwAuditLog: { label: 'Reading the FTW audit log', icon: 'read' },
  getMatildaActionLog: { label: 'Reading the action log', icon: 'read' },
  list_contacts: { label: 'Checking contacts', icon: 'read' },
  list_crm_contacts: { label: 'Checking CRM contacts', icon: 'read' },
  find_contact_by_email: { label: 'Looking up a contact', icon: 'read' },
  list_email_threads: { label: 'Scanning email threads', icon: 'email' },
  get_email_thread: { label: 'Reading an email thread', icon: 'email' },
  set_email_attachment_disposition: { label: 'Filing an email attachment', icon: 'email' },
  list_onboarding_sessions: { label: 'Checking onboarding sessions', icon: 'read' },
  get_onboarding_session: { label: 'Reading an onboarding session', icon: 'read' },
  update_onboarding_session: { label: 'Updating an onboarding session', icon: 'write' },
  convert_onboarding_session_to_sponsor: { label: 'Converting onboarding to a sponsor', icon: 'write' },
  recompute_pipeline: { label: 'Recomputing the pipeline', icon: 'pipeline' },
  resolve_exception: { label: 'Resolving an exception', icon: 'write' },
  hold_exception: { label: 'Holding an exception', icon: 'write' },
  update_plan_rules: { label: 'Updating the plan rules', icon: 'write' },
  edit_upload: { label: 'Editing an upload', icon: 'write' },
  create_sponsor: { label: 'Creating a sponsor', icon: 'write' },
  update_sponsor: { label: 'Updating a sponsor', icon: 'write' },
  add_email_contact: { label: 'Adding an email contact', icon: 'write' },
  remove_email_contact: { label: 'Removing an email contact', icon: 'write' },
  add_phone_contact: { label: 'Adding a phone contact', icon: 'write' },
  remove_phone_contact: { label: 'Removing a phone contact', icon: 'write' },
  refresh_view: { label: 'Refreshing the view', icon: 'refresh' },
  spawn_subagent: { label: 'Spawning a sub-agent', icon: 'agent' },
};

const WRITE_PREFIXES = [
  'update_',
  'create_',
  'add_',
  'remove_',
  'edit_',
  'send_',
  'void_',
  'queue_',
  'set_',
];

export function toolDisplay(toolName: string, args: unknown): ToolDisplay {
  if (toolName === 'spawn_subagent') {
    const name =
      args && typeof args === 'object' && typeof (args as { name?: unknown }).name === 'string'
        ? (args as { name: string }).name
        : null;
    return { label: name ? `Spawning sub-agent “${name}”` : 'Spawning a sub-agent', icon: 'agent' };
  }
  const known = TOOL_LABELS[toolName];
  if (known) return known;
  const humanized = toolName
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
  const isWrite = WRITE_PREFIXES.filter(prefix => toolName.startsWith(prefix)).length > 0;
  return { label: `Running ${humanized}`, icon: isWrite ? 'write' : 'read' };
}

export function formatToolCall(name: string, args: unknown): string {
  if (!args || typeof args !== 'object') return name;
  const tail = Object.entries(args as Record<string, unknown>)
    .slice(0, 3)
    .map(([k, v]) => {
      const s =
        typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : '…';
      const trimmed = s.length > 32 ? `${s.slice(0, 32)}…` : s;
      return `${k}=${trimmed}`;
    })
    .join(' ');
  return tail ? `${name}(${tail})` : name;
}

const ICON_PATHS: Record<ToolIconKind, string> = {
  read: 'M11 4a7 7 0 1 0 4.9 12L21 21M18 11a7 7 0 0 0-7-7',
  write: 'M17 3l4 4L8 20H4v-4L17 3z',
  email: 'M3 6h18v12H3zM3 7l9 6 9-6',
  agent:
    'M12 3v3M8 6h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zM9.5 11.5h.01M14.5 11.5h.01M4 12H2M22 12h-2',
  pipeline: 'M4 6h16M4 12h10M4 18h7M18 15l3 3-3 3',
  refresh: 'M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6',
};

interface ToolIconProps {
  kind: ToolIconKind;
  className?: string;
}

export const ToolIcon: FunctionComponent<ToolIconProps> = ({ kind, className = '' }) => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <path d={ICON_PATHS[kind]} />
  </svg>
);
